<script setup>
    import { onMounted } from 'vue';
    import { useInstantCredit } from '@/composables/front/checkout/useInstantCredit';
    import { useAbonoTracking } from '@/composables/front/analytics/useAbonoTracking';
    import InstantCreditOption from './InstantCreditOption.vue';
    import InstantCreditStatus from './InstantCreditStatus.vue';

    const props = defineProps({
        sessionId: {
            type: String,
            required: true,
        },
        tipoVenta: {
            type: String,
            default: 'abono',
        },
        importeCarrito: {
            type: Number,
            required: true,
        },
        gastosGestionUnitarios: {
            type: Number,
            default: 0,
        },
        numAbonados: {
            type: Number,
            default: 1,
        },
        store: {
            type: Object,
            required: true,
        },
        getVuexAlias: {
            type: Function,
            required: true,
        },
    });

    const {
        eligible,
        cuotasOptions,
        nCuotas,
        breakdown,
        phase,
        finalState,
        initiating,
        polling,
        error,
        selectCuota,
        initiatePayment,
        restartPayment,
        startPollingForOrder,
        stopPolling,
    } = useInstantCredit({
        sessionId: props.sessionId,
        tipoVenta: props.tipoVenta,
        importeCarrito: props.importeCarrito,
        gastosGestionUnitarios: props.gastosGestionUnitarios,
        numAbonados: props.numAbonados,
        store: props.store,
        getVuexAlias: props.getVuexAlias,
    });

    const tracking = useAbonoTracking({
        store: props.store,
        getVuexAlias: props.getVuexAlias,
    });

    onMounted(() => {
        if (eligible.value) {
            tracking.trackIcSelectorViewed();
        }
    });

    const onSelectCuota = n => {
        selectCuota(n);
    };

    const onPay = () => {
        initiatePayment();
    };

    const onRetry = () => {
        // Per WU-2 design D2: a retry click must dispatch a fresh POST with a new
        // tpv_order, aborting the prior AbortController. `restartPayment` chains
        // `retryPayment` (idempotent reset, aborts prior polling) with
        // `initiatePayment` (fresh POST).
        restartPayment();
    };
</script>

<template>
    <div v-if="eligible" data-test="instant-credit-container">
        <InstantCreditOption
            v-if="phase === 'cuotas'"
            :cuotas="cuotasOptions"
            :n-cuotas="nCuotas"
            :breakdown="breakdown"
            :num-abonados="numAbonados"
            :loading="initiating"
            @select="onSelectCuota"
            @pay="onPay"
        />
        <InstantCreditStatus
            v-else
            :phase="phase"
            :final-state="finalState"
            :error="error"
            :polling="polling"
            @retry="onRetry"
        />
    </div>
</template>
